import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Footer from "../components/home/Footer";
import Header from "../components/home/Header";
import MiniCart from "../components/home/MiniCart";
import SEOHead from "../components/seo/SEOHead";

function formatDate(date) {
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric"
  }).format(new Date(date));
}

function getArticleSections(post) {
  return [
    {
      title: "Why this matters for your plants",
      body: `${post.content} Healthy soil is the foundation of stronger roots, balanced growth, and better long-term plant performance.`
    },
    {
      title: "How to apply this in your garden",
      body: "Start with the root zone. Loosen the top layer of soil, add organic nutrition evenly, and water gently so the nutrients can begin moving through the growing media."
    },
    {
      title: "What to watch after application",
      body: "Look for steady new growth, richer leaf colour, improved moisture retention, and better flowering or fruiting over the next few weeks. Organic inputs work gradually, so consistency matters more than heavy one-time feeding."
    }
  ];
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [posts, setPosts] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartItems] = useState([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let active = true;

    fetch("/json/blogs.json")
      .then((response) => response.json())
      .then((data) => {
        if (active) setPosts(data);
      })
      .catch(() => {
        if (active) setPosts([]);
      });

    return () => {
      active = false;
    };
  }, []);

  const post = useMemo(() => posts?.find((item) => item.slug === slug), [posts, slug]);

  const schema = useMemo(() => {
    if (!post) return [];

    return [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt,
        "image": post.image,
        "datePublished": post.date,
        "dateModified": post.date,
        "author": {
          "@type": "Organization",
          "name": post.author
        },
        "publisher": {
          "@type": "Organization",
          "name": "Paras Organics",
          "logo": {
            "@type": "ImageObject",
            "url": "https://parasorganics.com/logo512.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://parasorganics.com/blog/${post.slug}`
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://parasorganics.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://parasorganics.com/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": post.title,
            "item": `https://parasorganics.com/blog/${post.slug}`
          }
        ]
      }
    ];
  }, [post]);

  if (posts && !post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <>
      {post && (
        <SEOHead
          title={post.title}
          description={post.excerpt}
          path={`/blog/${post.slug}`}
          image={post.image}
          type="article"
          publishedTime={post.date}
          modifiedTime={post.date}
          schema={schema}
        />
      )}
      <Header scrolled={scrolled} cartItems={cartItems} onCartClick={() => setCartOpen((open) => !open)} />
      {cartOpen && <MiniCart cartItems={cartItems} onClose={() => setCartOpen(false)} />}
      <main className="blog-detail-page">
        {post && (
          <article className="blog-detail-article" itemScope itemType="https://schema.org/BlogPosting">
            <div className="blog-detail-shell">
              <nav className="blog-detail-breadcrumb" aria-label="Breadcrumb">
                <Link to="/">Home</Link>
                <span>/</span>
                <Link to="/blog">Blog</Link>
                <span>/</span>
                <span>{post.category}</span>
              </nav>

              <header className="blog-detail-header">
                <h1 itemProp="headline">{post.title}</h1>
                <div className="blog-detail-meta">
                  <span itemProp="articleSection">{post.category}</span>
                  <span>{post.readTime}</span>
                  <time dateTime={post.date} itemProp="datePublished">{formatDate(post.date)}</time>
                </div>
              </header>

              <figure className="blog-detail-feature">
                <img className="blog-detail-image" src={post.image} alt={post.title} itemProp="image" />
              </figure>

              <div className="blog-detail-content" itemProp="articleBody">
                <p className="blog-detail-intro" itemProp="description">{post.excerpt}</p>
                {getArticleSections(post).map((section, index) => (
                  <section key={section.title}>
                    <h2>{index + 1}. {section.title}</h2>
                    <p>{section.body}</p>
                  </section>
                ))}
              </div>
            </div>
          </article>
        )}
      </main>
      <Footer />
    </>
  );
}
